import React, {useEffect, useMemo, useState} from 'react';
import {Flex, Form, Input, message, Select, Spin} from "antd";
import debounce from 'lodash/debounce';
import UserService from "../../../../../services/UserService";
import {useDispatch, useSelector} from "react-redux";
import {LocalName} from "../../../../../utils/LocalName";
import ContractService from "../../../../../services/ContractService";
import classes from "../../Courses/CreateCourse/CreateCourse.module.css";
import BlockLoading from "../../../../../components/LoadingComponents/BlockLoading";
import FormItem from "antd/es/form/FormItem";
import {clrs} from "../../../../../constants/colors";
import IntlMessage, {IntlMessageText} from "../../../../../components/IntlMessage/IntlMessage";
import GreyButton from "../../../../../components/UI/Buttons/GreyButton";

const ContractCreate = () => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const firstName = Form.useWatch('firstName', form);
    const lastName = Form.useWatch('lastName', form);
    const middleName = Form.useWatch('middleName', form);
    const email = Form.useWatch('email', form);
    const categoryId = Form.useWatch('categoryId', form);

    const [categories, setCategories] = useState([]);
    const categoriesList = useSelector(state => state.contractCategoriesList);

    const [isLoadingCreate, setIsLoadingCreate] = useState(false);

    // ТЗ updated п.6.2: выбор существующего ученика вместо ручного ввода ФИО
    const [students, setStudents] = useState([]);
    const [isLoadingStudents, setIsLoadingStudents] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);

    useEffect(() => {
        setCategories(
            categoriesList.categories.map(category => {
                return { value: category.id, label: LocalName.getName(category) }
            })
        )
    }, [categoriesList.categories]);

    useEffect(() => {
        searchStudents("");
    }, []);

    async function searchStudents(searchText) {
        setIsLoadingStudents(true);
        try {
            const response = await UserService.getStudentsSearchable(1, 50, searchText);
            setStudents(
                response.data.map(student => ({
                    value: student.id,
                    label: student.fullName + (student.email ? ` — ${student.email}` : ''),
                    fullName: student.fullName,
                    email: student.email
                }))
            );
        } catch (e) {
            message.error(<IntlMessage id={'studentsFetchError'}/>, 5);
        } finally {
            setIsLoadingStudents(false);
        }
    }

    // debounce создаём один раз, иначе он пересоздаётся на каждый рендер и не работает
    const debouncedSearchStudents = useMemo(() => debounce(searchStudents, 500), []);

    // ФИО в системе хранится как «Фамилия Имя Отчество» — раскладываем в поля договора,
    // чтобы оператор видел, что именно попадёт в документ, и мог поправить
    function handleSelectStudent(userId, option) {
        setSelectedUserId(userId);
        if (!option) {
            return;
        }
        const parts = (option.fullName || '').trim().split(/\s+/);
        form.setFieldsValue({
            lastName: parts[0] || '',
            firstName: parts[1] || '',
            middleName: parts[2] || '',
            email: option.email || ''
        });
    }

    function handleCreate() {
        form.validateFields()
            .then(async () => {
                message.loading(<IntlMessage id={'loading'}/>, 0);
                setIsLoadingCreate(true);
                await ContractService.saveContract(firstName, lastName, middleName, categoryId, email, selectedUserId)
                    .then((response) => {
                        message.destroy();
                        message.success(<IntlMessage id={'creation'}/>, 5);
                        // связка с аккаунтом раньше падала молча — теперь предупреждаем явно
                        if (!response?.data?.user) {
                            message.warning(<IntlMessage id={'contracts.notLinkedToAccount'}/>, 7);
                        }
                        form.resetFields();
                        setSelectedUserId(null);
                    })
                    .catch(() => {
                        message.destroy();
                        message.error(<IntlMessage id={'error'}/>, 5);
                    })
                    .finally(() => {
                        setIsLoadingCreate(false);
                    })
            })
            .catch(() => {
                console.log("Validation error!")
            })
    }

    const contractCreate = useSelector(state => state.contractCreate);

    return (
        <Form form={form} className={classes.form}>
            <BlockLoading isLoading={isLoadingCreate}/>
            {/* ТЗ updated п.6.2: выбрать существующего ученика — договор свяжется с его аккаунтом.
                Если ученика ещё нет в системе, поля ниже заполняются вручную, как раньше. */}
            <FormItem label={<IntlMessage id={'contracts.pickStudent'}/>} labelCol={{span: 24}}
                      className={classes.formInputBlock}>
                <Select
                    showSearch
                    allowClear
                    filterOption={false}
                    value={selectedUserId}
                    onSearch={debouncedSearchStudents}
                    onChange={handleSelectStudent}
                    onClear={() => setSelectedUserId(null)}
                    loading={isLoadingStudents}
                    notFoundContent={isLoadingStudents ? <Spin size="small"/> : null}
                    options={students}
                    placeholder={IntlMessageText.getText({id: 'contracts.pickStudent'})}
                />
            </FormItem>
            <FormItem rules={[{
                required: true,
                message: <IntlMessage id={'requiredField'}/>,
            },
            ]} label={<IntlMessage id={'firstName'}/>} labelCol={{ span: 24 }} className={classes.formInputBlock} name="firstName">
                <Input rootClassName={classes.formInput} placeholder={IntlMessageText.getText({id: "firstName"})} />
            </FormItem>
            <FormItem rules={[{
                required: true,
                message: <IntlMessage id={'requiredField'}/>,
            },
            ]} label={<IntlMessage id={'lastName'}/>} labelCol={{ span: 24 }} className={classes.formInputBlock} name="lastName">
                <Input rootClassName={classes.formInput} placeholder={IntlMessageText.getText({id: "lastName"})} />
            </FormItem>
            <FormItem label={<IntlMessage id={'middleName'}/>} labelCol={{ span: 24 }} className={classes.formInputBlock} name="middleName">
                <Input rootClassName={classes.formInput} placeholder={IntlMessageText.getText({id: "middleName"})} />
            </FormItem>
            <FormItem label={<IntlMessage id={'email'}/>} labelCol={{ span: 24 }} className={classes.formInputBlock} name="email">
                <Input rootClassName={classes.formInput} placeholder={IntlMessageText.getText({id: "email"})} />
            </FormItem>
            <FormItem rules={[{
                required: true,
                message: <IntlMessage id={'requiredField'}/>,
            },
            ]} label={<IntlMessage id={'category'}/>} labelCol={{ span: 24 }} className={classes.formInputBlock} name="categoryId">
                <Select
                    options={categories}
                />
            </FormItem>
            <FormItem className={classes.formInputBlock}>
                <Flex align={"flex-start"} justify={"flex-start"} gap={20} style={{width: "100%"}}>
                    <GreyButton onClick={handleCreate} backgroundColor={clrs.blackerBlue} fontColor={clrs.white}><IntlMessage id={'create'}/></GreyButton>
                </Flex>
            </FormItem>
        </Form>
    );
};

export default ContractCreate;